/**
 * Rounded-Manhattan connector, in the 0–100 viewBox space.
 *
 * Leaves the source vertically, jogs horizontally at the midpoint between the
 * two endpoints, and enters the target vertically — with quarter-round fillets
 * at all four corners. Axis-aligned pairs get a plain straight segment.
 *
 * `r` is in viewBox units, so on a canvas 100 units wide r = 2.5 is 2.5% of
 * the width. It is clamped to half the available run so short segments do not
 * produce overlapping control points.
 */
export function smoothStep(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  r = 2.5,
): string {
  const dx = Math.abs(x2 - x1)
  const dy = Math.abs(y2 - y1)

  if (dx < 0.5 || dy < 0.5) {
    return `M ${x1} ${y1} L ${x2} ${y2}`
  }

  const rr = Math.min(r, dx / 2, dy / 2)
  const sx = x2 > x1 ? 1 : -1
  const sy = y2 > y1 ? 1 : -1
  const midY = (y1 + y2) / 2

  return [
    `M ${x1} ${y1}`,
    `L ${x1} ${midY - sy * rr}`,
    `Q ${x1} ${midY} ${x1 + sx * rr} ${midY}`,
    `L ${x2 - sx * rr} ${midY}`,
    `Q ${x2} ${midY} ${x2} ${midY + sy * rr}`,
    `L ${x2} ${y2}`,
  ].join(' ')
}

/** Tracer dot colour alternates per edge so adjacent lanes stay distinguishable. */
export function pulseColor(index: number): string {
  return index % 2 === 0 ? '#5B9DF5' : '#38D0E8'
}

/** Stable DOM id for an edge's motion path. */
export function flowPathId(from: string, to: string): string {
  return `tl-flow-${from}-${to}`
}
